import { useCallback, useState } from 'react'
import moment from 'moment'

import DataTable from '../../../../components/DataTable'
import Container from '../../../../components/Container'
import Form, { Input, Select } from '../../../../components/Form'
import { DateInput } from '../../../../components/Form/date'
import Button from '../../../../components/Button'
import api from 'services/api'
import { useToast } from 'hooks/toast'
import { useLoading } from 'hooks/loading'

import { nameEntity, nameSource } from '../domain/info'
import { headers } from '../domain/headers'
import { breadcrumbList } from '../domain/breadcrumb'
import {
  apiDownloadPdf,
  apiDownloadPdfBatch,
  apiDownloadXml,
  apiDownloadXmlBatch,
  apiSync
} from '../domain/api'
import { FilterContainer } from './styles'

interface ISearchParameters {
  start_date?: Date
  end_date?: Date
  CNPJ?: string
  nNF?: string
  cSitNFe?: string
}

const statusOptions = [
  { name: 'Normal', value: 1 },
  { name: 'Denegada', value: 2 },
  { name: 'NF-e cancelada', value: 3 }
]

const hasDownloadableXml = (item: {
  download?: boolean | number | string | null
  schema?: string
}): boolean => {
  return (
    item.schema === 'procNFe' ||
    item.download === true ||
    Number(item.download) === 1
  )
}

const hasDownloadablePdf = (item: {
  download?: boolean | number | string | null
  schema?: string
}): boolean => {
  return item.schema === 'procNFe'
}

const InvoicesIssuedForCnpjList = (): JSX.Element => {
  const [startDate, setStartDate] = useState<Date>()
  const [endDate, setEndDate] = useState<Date>()
  const [cnpj, setCnpj] = useState<string>()
  const [invoiceNumber, setInvoiceNumber] = useState<string>()
  const [status, setStatus] = useState<string>()
  const [parameters, setParameters] = useState<any[]>()

  const { addToast } = useToast()
  const { activeLoading, disableLoading, loading } = useLoading()

  const buildSearchParameters = useCallback((): ISearchParameters => {
    return {
      start_date: startDate ? moment(startDate).toDate() : undefined,
      end_date: endDate ? moment(endDate).toDate() : undefined,
      CNPJ: cnpj || undefined,
      nNF: invoiceNumber || undefined,
      cSitNFe: status || undefined
    }
  }, [cnpj, endDate, invoiceNumber, startDate, status])

  const buildBatchQuery = useCallback((): string => {
    const params = buildSearchParameters()
    const query: string[] = []

    if (params.start_date) {
      query.push(`start_date=${moment(params.start_date).format('YYYY-MM-DD')}`)
    }
    if (params.end_date) {
      query.push(`end_date=${moment(params.end_date).format('YYYY-MM-DD')}`)
    }
    if (params.CNPJ) {
      query.push(`CNPJ=${encodeURIComponent(params.CNPJ)}`)
    }
    if (params.nNF) {
      query.push(`nNF=${encodeURIComponent(params.nNF)}`)
    }
    if (params.cSitNFe) {
      query.push(`cSitNFe=${params.cSitNFe}`)
    }

    return query.join('&')
  }, [buildSearchParameters])

  const downloadBlob = useCallback(
    async (url: string, fallbackFileName: string, mimeType: string) => {
      activeLoading()
      try {
        const response = await api.get(url, { responseType: 'blob' })
        const contentDisposition = response.headers['content-disposition']
        const fileNameMatch = contentDisposition?.match(
          /filename\*?=(?:UTF-8''|")?([^";]+)/i
        )
        const fileName = fileNameMatch?.[1]
          ? decodeURIComponent(fileNameMatch[1].replace(/"/g, '').trim())
          : fallbackFileName
        const blob =
          response.data instanceof Blob
            ? response.data
            : new Blob([response.data], { type: mimeType })
        const objectUrl = window.URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = objectUrl
        anchor.download = fileName
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
        window.URL.revokeObjectURL(objectUrl)
        addToast({ type: 'success', title: 'Download realizado com sucesso' })
      } catch (error: any) {
        const text = await error?.response?.data?.text?.()
        let errorMessage = 'Erro ao realizar o download'
        if (text) {
          try {
            const payload = JSON.parse(text)
            errorMessage = payload?.message || errorMessage
          } catch {
            errorMessage = text
          }
        }
        addToast({ type: 'error', title: errorMessage })
      } finally {
        disableLoading()
      }
    },
    [activeLoading, addToast, disableLoading]
  )

  const onSearch = () => {
    setParameters([buildSearchParameters()])
  }

  const handleSync = async () => {
    activeLoading()
    try {
      const { data } = await api.post(apiSync())
      const failures = (data?.results || []).filter(
        (result: { error?: string }) => result.error
      )
      const successes = (data?.results || []).filter(
        (result: { error?: string; lastNsu?: number }) =>
          !result.error && result.lastNsu !== undefined
      )

      if (failures.length) {
        addToast({
          type: 'error',
          title: 'Sincronização com falhas',
          description: failures
            .map(
              (result: { fiscalSettingId: number; error?: string }) =>
                `Config. fiscal ${result.fiscalSettingId}: ${result.error}`
            )
            .join(' | ')
        })
      }

      if (successes.length) {
        addToast({
          type: 'success',
          title: 'Sincronização concluída',
          description: `${successes.length} configuração(ões) fiscal(is) sincronizada(s).`
        })
        setParameters([buildSearchParameters()])
      }
    } catch (error: any) {
      addToast({
        type: 'error',
        title: error?.response?.data?.message || 'Erro ao sincronizar notas'
      })
    } finally {
      disableLoading()
    }
  }

  const handleDownloadZip = async (type: 'xml' | 'pdf') => {
    if (!startDate || !endDate) {
      addToast({
        type: 'error',
        title: 'Informe o período',
        description: 'Para baixar o ZIP, informe a data inicial e final.'
      })
      return
    }

    const query = buildBatchQuery()
    const url =
      type === 'xml' ? apiDownloadXmlBatch(query) : apiDownloadPdfBatch(query)

    await downloadBlob(url, `notas-recebidas-${type}.zip`, 'application/zip')
  }

  const handleDownloadXml = useCallback(
    (item: { id: number; chNFe: string }) => {
      downloadBlob(
        apiDownloadXml(Number(item.id)),
        `${item.chNFe}-nfe.xml`,
        'application/xml'
      )
    },
    [downloadBlob]
  )

  const handleDownloadPdf = useCallback(
    (item: { id: number; chNFe: string }) => {
      downloadBlob(
        apiDownloadPdf(Number(item.id)),
        `${item.chNFe}.pdf`,
        'application/pdf'
      )
    },
    [downloadBlob]
  )

  const tableHeaders = headers.filter(header => header.field !== 'actions')

  const customHeaders = [
    {
      name: 'Ações',
      field: 'downloads',
      sortable: false,
      element: (item: {
        id: number
        chNFe: string
        schema?: string
        download?: boolean | number | string | null
      }) => (
        <div className="d-flex gap-2">
          {hasDownloadableXml(item) && (
            <button
              type="button"
              className="btn btn-sm btn-light-success"
              title="Download XML"
              onClick={() => handleDownloadXml(item)}
            >
              XML
            </button>
          )}
          {hasDownloadablePdf(item) && (
            <button
              type="button"
              className="btn btn-sm btn-light-primary"
              title="Download PDF"
              onClick={() => handleDownloadPdf(item)}
            >
              PDF
            </button>
          )}
        </div>
      )
    }
  ]

  return (
    <Container
      pageTitle="Notas emitidas contra CNPJ"
      breadcrumb={breadcrumbList}
      tools={[]}
    >
      <FilterContainer>
        <Form onSubmit={onSearch} defaultValues={{}} className="col-md-12">
          <div className="row">
            <div className="col-md-2 pe-8">
              <DateInput
                label="Data inicial"
                name="start_date"
                onChange={date => setStartDate(date)}
                selected={startDate}
                controlled
              />
            </div>
            <div className="col-md-2 pe-8">
              <DateInput
                label="Data final"
                name="end_date"
                onChange={date => setEndDate(date)}
                selected={endDate}
                controlled
              />
            </div>
            <div className="col-md-2 pe-8">
              <Input
                label="CNPJ emitente"
                name="CNPJ"
                controlled
                value={cnpj || ''}
                onChange={e => setCnpj(e.target.value)}
              />
            </div>
            <div className="col-md-2 pe-8">
              <Input
                label="Nº NF"
                name="nNF"
                controlled
                value={invoiceNumber || ''}
                onChange={e => setInvoiceNumber(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <Select
                label="Situação"
                name="cSitNFe"
                options={statusOptions}
                onChange={e => setStatus(e.target.value)}
                value={status}
                controlled
                blank
                defaultValue={''}
              />
            </div>
          </div>
          <footer className="col-md-12 d-flex justify-content-end pb-3 gap-3 flex-wrap">
            <button
              type="reset"
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setStartDate(undefined)
                setEndDate(undefined)
                setCnpj(undefined)
                setInvoiceNumber(undefined)
                setStatus(undefined)
                setParameters(undefined)
              }}
            >
              LIMPAR
            </button>
            <Button
              type="button"
              className="btn btn-info btn-sm"
              disabled={loading}
              onClick={handleSync}
            >
              SINCRONIZAR AGORA
            </Button>
            <Button
              type="button"
              className="btn btn-outline-primary btn-sm"
              disabled={loading}
              onClick={() => handleDownloadZip('xml')}
            >
              BAIXAR ZIP XML
            </Button>
            <Button
              type="button"
              className="btn btn-outline-primary btn-sm"
              disabled={loading}
              onClick={() => handleDownloadZip('pdf')}
            >
              BAIXAR ZIP PDF
            </Button>
            <button type="submit" className="btn btn-primary btn-sm">
              BUSCAR
            </button>
          </footer>
        </Form>
      </FilterContainer>

      {parameters && (
        <DataTable
          entity={nameEntity}
          source={nameSource}
          headers={[
            ...tableHeaders,
            { name: 'Ações', field: 'downloads', sortable: false, custom: true }
          ]}
          customHeaders={customHeaders}
          format={{ orderBy: 'dhEmi' }}
          orderBySort="DESC"
          searchParameters={parameters}
        />
      )}
    </Container>
  )
}

export default InvoicesIssuedForCnpjList
